import React from 'react';
import { getApiBase } from '../apiBase';

const TEXT = 'rgba(255,255,255,0.96)';
const MUTED = 'rgba(255,255,255,0.55)';
const ACCENT = '#0a84ff';
const SURFACE = 'rgba(255,255,255,0.04)';
const BORDER = 'rgba(255,255,255,0.08)';

const CARD = { background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 13, padding: 14, marginBottom: 12 };

// ── EVERY AUTOMATION LIVES IN BOOKS (Braeden 8/13) ──────────────────────────
// "everything you added needs to be inside books, so when you click into books,
//  thats what pops up"
//
// The catalogue sat on the company page; this is where he wants it. Same list,
// same cadence grouping — but the Ready/needs state is NOT hardcoded here. It
// comes from GET /api/automations/status, which probes Stripe, Twilio, Resend
// and Maps for real. A key that is present but rejected reads as not running:
// presence is not proof, and a page that claims work it isn't doing is worse
// than one that admits the gap.
const CAP_GROUPS = [
  ['Every day', [
    ['missed_calls', 'Missed calls', 'Texts back in seconds so the job is not lost'],
    ['calendar_invites', 'Calendar invites', 'Book jobs & send reminders'],
    ['text_reminders', 'Text reminders', 'Send & receive customer SMS'],
    ['new_leads', 'New leads', 'Every lead into one list, whatever the source'],
    ['days_route', "Today's route", 'Puts the day in drive order, least backtrack'],
    ['job_photos', 'Job photos', 'Before & after saved to the job — your proof'],
  ]],
  ['Every job', [
    ['quotes', 'Quotes & estimates', 'Priced from the scope, you edit before it goes'],
    ['invoices', 'Invoices', 'Create, send & chase invoices'],
    ['payments', 'Payments', 'Pay link — card, bank, or financing'],
    ['crew_hours', 'Crew hours', 'Clock in/out per job — feeds payroll & costing'],
  ]],
  ['Every week', [
    ['quote_chase', 'Quotes with no reply', 'Nudges the ones that went quiet'],
    ['overdue_invoices', 'Overdue invoices', 'Reminders that escalate until you are paid'],
    ['expenses', 'Receipts & spend', 'Logs every dollar out, categorised'],
    ['supplies', 'Supplies & materials', 'Reorder before you run out — fuel, dump, bags'],
    ['lead_sources', 'Where jobs came from', 'What each source cost vs the work it won'],
  ]],
  ['Every month', [
    ['reviews', 'Reviews', 'Asks happy customers, replies to every one'],
    ['email_promotions', 'Email promotions', 'Send promos & replies'],
    ['payroll', 'Payroll & contractors', 'Hours to pay, and who is a 1099'],
    ['repeat_customers', 'Repeat customers', 'Service agreements billed on schedule'],
    ['vehicle_upkeep', 'Truck & equipment', 'Service due by mileage or hours'],
    ['gbp_posts', 'Google Business page', 'Keeps the map listing active with real posts'],
  ]],
  ['Every quarter', [
    ['taxes', 'Quarterly taxes', 'Sets aside as you earn, files on time'],
    ['price_check', 'Price & margin check', 'Flags jobs where your cost outran the price'],
    ['vendor_papers', 'Sub & vendor paperwork', 'W-9s and insurance certs before they work'],
  ]],
  ['Every year', [
    ['renewals', 'Renewals & filings', 'License, insurance, vehicle reg, state report'],
    ['year_end', 'Year-end books', '1099s, W-2s, and the package your CPA wants'],
    ['winback', 'Win back old customers', 'Reaches out to anyone quiet 12 months'],
  ]],
];

export default function ExpensesPanel({ ownerId, onClose }) {
  const [scanning, setScanning] = React.useState(false);
  const [result, setResult] = React.useState(null); // { categorized, sample, pnl }
  const [scanErr, setScanErr] = React.useState('');
  const [plaidNote, setPlaidNote] = React.useState(false);
  const fileRef = React.useRef(null);
  const [autos, setAutos] = React.useState(null);   // live readiness, server-probed

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${getApiBase()}/api/automations/status`);
        const j = await r.json();
        if (alive && j && j.ok) setAutos(j);
      } catch (_) { /* unknown beats a wrong claim — rows stay neutral */ }
    })();
    return () => { alive = false; };
  }, []);

  // Real scan: photo → /api/owner/expenses/scan (Gemini OCR → categorize → P&L).
  const onFile = React.useCallback(async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setScanning(true); setScanErr(''); setResult(null);
    try {
      const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onloadend = () => res(String(r.result || '')); r.onerror = () => rej(new Error('read')); r.readAsDataURL(f); });
      const resp = await fetch(`${getApiBase()}/api/owner/expenses/scan`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ownerId, imageBase64: dataUrl, mimeType: f.type || 'image/jpeg' }) });
      const data = await resp.json();
      if (data && data.ok) setResult(data);
      else setScanErr((data && data.error) || "Couldn't read that — try a clearer photo.");
    } catch (_) { setScanErr("Couldn't read that — try again."); }
    setScanning(false);
    try { if (fileRef.current) fileRef.current.value = ''; } catch (_) {}
  }, [ownerId]);

  const connectPlaid = React.useCallback(async () => {
    setPlaidNote(true);
    try { await fetch(`${getApiBase()}/api/expenses/plaid/status?owner=${encodeURIComponent(ownerId || '')}`); } catch (_) {}
  }, [ownerId]);

  return (
    <div className="agent-detail" onClick={onClose}>
      <div className="agent-detail__panel" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="agent-detail__close" onClick={onClose} aria-label="Close">×</button>
        <div className="agent-detail__title">Expenses &amp; books</div>
        <div className="agent-detail__cadence">Weekly</div>
        <p className="agent-detail__desc">Track every dollar out so your books and taxes are ready.</p>

        {/* The automation list moved to BooksView.jsx — a real page (8/13).
            Inside this dialog its rows were hardcoded white and vanished in day
            theme. This panel goes back to being what it is good at: logging an
            expense. */}
        <div className="agent-detail__section">Log an expense</div>

        <div style={CARD}>
          <div style={{ color: TEXT, fontWeight: 700, marginBottom: 4 }}>Scan a statement or receipt</div>
          <div style={{ color: MUTED, fontSize: 13, marginBottom: 10 }}>Snap a photo — Aria reads the line items and categorizes them.</div>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFile} />
          <button type="button" className="sched-refresh" onClick={() => fileRef.current && fileRef.current.click()} disabled={scanning}>{scanning ? 'Reading…' : 'Choose a photo'}</button>
          {scanErr && <div className="sched-warn" style={{ marginTop: 10 }}>{scanErr}</div>}
          {result && (
            <div style={{ marginTop: 10 }}>
              <div className="sched-ok">Read {result.categorized} line item{result.categorized === 1 ? '' : 's'} from that.</div>
              {Array.isArray(result.sample) && result.sample.length > 0 && (
                <ul className="sched-events" style={{ marginTop: 8 }}>
                  {result.sample.slice(0, 8).map((t, i) => (
                    <li className="sched-event" key={i}>
                      <span className="sched-event__t">{t.desc || t.description || 'Item'}</span>
                      <span className="sched-event__m">{t.category || 'uncategorized'}{t.amount != null ? ` · $${Math.abs(Number(t.amount)).toLocaleString()}` : ''}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div style={CARD}>
          <div style={{ color: TEXT, fontWeight: 700, marginBottom: 4 }}>Connect bank / card (Plaid)</div>
          <div style={{ color: MUTED, fontSize: 13 }}>Auto-import transactions so nothing slips through.</div>
          <button type="button" className="agent-detail__cta" onClick={connectPlaid}>Connect bank / card</button>
          {plaidNote && <div className="sched-warn" style={{ marginTop: 10 }}>Bank connect (Plaid) switches on with your Plaid keys.</div>}
        </div>

        <div className="agent-detail__note" style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 12, color: MUTED }}>
          <span style={{ color: ACCENT }}>Either way</span> feeds your P&amp;L and quarterly taxes.
        </div>
      </div>
    </div>
  );
}
